/**
 * InitiateVerificationEmailDialog Component
 * Enhanced dialog for initiating verification emails
 * - Employment: Company database > Candidate provided HR > Agent manual entry
 * - Education: University database > Candidate provided > Agent manual entry
 * - Reference: Reference contact with optional acknowledgement
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  Mail, Send, Eye, Building2, GraduationCap, Users, Plus, X, 
  RefreshCw, CheckCircle, Database, User, Edit, Save, AlertCircle,
  Paperclip, FileText, Clock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Email source types
const EMAIL_SOURCES = {
  database: { label: 'From Database', icon: Database, priority: 1 },
  candidate_provided: { label: 'Candidate Provided', icon: User, priority: 2 },
  manual: { label: 'Enter Manually', icon: Edit, priority: 3 },
};

export function InitiateVerificationEmailDialog({
  open,
  onOpenChange,
  check,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('email');
  
  // Email options state
  const [emailOptions, setEmailOptions] = useState([]);
  const [selectedEmailSource, setSelectedEmailSource] = useState('');
  const [selectedEmail, setSelectedEmail] = useState('');
  const [selectedRecipientName, setSelectedRecipientName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualRecipientName, setManualRecipientName] = useState('');
  const [saveToDatabase, setSaveToDatabase] = useState(true);
  
  // CC emails - pre-populate with logged-in user's email
  const [ccEmails, setCcEmails] = useState([]);
  const [newCcEmail, setNewCcEmail] = useState('');
  const [defaultCcApplied, setDefaultCcApplied] = useState(false);
  
  // Additional options
  const [customRemarks, setCustomRemarks] = useState('');
  const [sendAcknowledgement, setSendAcknowledgement] = useState(false); // For reference checks
  
  // Email suggestions (unverified)
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [confirmSuggestion, setConfirmSuggestion] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  
  // Documents
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  
  // Preview
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Email history
  const [emailHistory, setEmailHistory] = useState([]);

  const checkType = check?.check_type?.toLowerCase() || '';
  const isEmployment = checkType.includes('employment');
  const isEducation = checkType.includes('education');
  const isReference = checkType.includes('reference');
  const isResend = check?.initiation_email_sent === true;

  // Load email options when dialog opens
  useEffect(() => {
    if (open && check) {
      loadEmailOptions();
      loadDocuments();
      loadEmailHistory();
      // Pre-populate CC with logged-in user's email
      if (!defaultCcApplied) {
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const userEmail = payload.email || '';
            if (userEmail && userEmail.includes('@')) {
              setCcEmails(prev => prev.includes(userEmail) ? prev : [userEmail, ...prev]);
              setDefaultCcApplied(true);
            }
          }
        } catch (e) { /* ignore */ }
      }
    }
    if (!open) {
      setDefaultCcApplied(false);
    }
  }, [open, check]);

  const loadEmailOptions = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/initiate-email/options`, {
        case_id: check.case_id,
        check_id: check.check_id || check.id,
        check_type: check.check_type,
      });
      
      const options = response.data.email_options || [];
      setEmailOptions(options);
      
      // Set suggestions (unverified)
      setEmailSuggestions(response.data.email_suggestions || []);
      
      // Auto-select first available option (database first)
      if (options.length > 0) {
        const dbOption = options.find(o => o.source === 'database');
        const candidateOption = options.find(o => o.source === 'candidate_provided');
        
        if (dbOption) {
          setSelectedEmailSource('database');
          setSelectedEmail(dbOption.email);
          setSelectedRecipientName(dbOption.name || '');
        } else if (candidateOption) {
          setSelectedEmailSource('candidate_provided');
          setSelectedEmail(candidateOption.email);
          setSelectedRecipientName(candidateOption.name || '');
        } else {
          setSelectedEmailSource('manual');
        }
      } else {
        setSelectedEmailSource('manual');
      }
    } catch (error) {
      console.error('Failed to load email options:', error);
      toast.error('Failed to load email options');
      setSelectedEmailSource('manual');
    } finally {
      setLoading(false);
    }
  };

  const loadEmailHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/initiate-email/history/${check.case_id}/${check.check_id || check.id}`);
      setEmailHistory(response.data || []);
    } catch (error) {
      setEmailHistory([]);
    }
  };


  const loadDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/verification-email/check-documents/${check.case_id}/${check.check_id || check.id}`);
      setAvailableDocuments(response.data.documents || []);
      
      // Auto-select declaration form
      const declaration = response.data.documents?.find(d => d.type === 'declaration');
      if (declaration) {
        setSelectedDocuments([declaration.id]);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  const handleEmailSourceChange = (source) => {
    setSelectedEmailSource(source);
    setConfirmSuggestion(false);
    setSelectedSuggestion(null);
    
    if (source === 'manual') {
      setSelectedEmail('');
      setSelectedRecipientName('');
    } else if (source === 'suggestion') {
      // Don't auto-fill for suggestions - require explicit selection
      setSelectedEmail('');
      setSelectedRecipientName('');
    } else {
      const option = emailOptions.find(o => o.source === source);
      if (option) {
        setSelectedEmail(option.email);
        setSelectedRecipientName(option.name || '');
      }
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setSelectedEmailSource('suggestion');
    setManualEmail(suggestion.email);
    setManualRecipientName(isEmployment ? 'HR Department' : 'Registrar Office');
    // Show confirmation warning
    setConfirmSuggestion(false);
  };

  const handleConfirmSuggestion = () => {
    if (selectedSuggestion) {
      setConfirmSuggestion(true);
      toast.warning('Please verify this email is correct before sending sensitive data');
    }
  };

  const handleAddCc = () => {
    if (newCcEmail && newCcEmail.includes('@') && !ccEmails.includes(newCcEmail)) {
      setCcEmails([...ccEmails, newCcEmail]);
      setNewCcEmail('');
    }
  };

  const handleRemoveCc = (email) => {
    setCcEmails(ccEmails.filter(e => e !== email));
  };

  const handleToggleDocument = (docId) => {
    if (selectedDocuments.includes(docId)) {
      setSelectedDocuments(selectedDocuments.filter(id => id !== docId));
    } else {
      setSelectedDocuments([...selectedDocuments, docId]);
    }
  };

  const getRecipientEmail = () => {
    if (selectedEmailSource === 'manual' || selectedEmailSource === 'suggestion') {
      return manualEmail;
    }
    return selectedEmail;
  };

  const getRecipientName = () => {
    if (selectedEmailSource === 'manual' || selectedEmailSource === 'suggestion') {
      return manualRecipientName;
    }
    return selectedRecipientName;
  };

  const canSendEmail = () => {
    const email = getRecipientEmail();
    if (!email) return false;
    // If using suggestion, require confirmation
    if (selectedEmailSource === 'suggestion' && !confirmSuggestion) return false;
    return true;
  };

  const handlePreview = async () => {
    const email = getRecipientEmail();
    if (!email) {
      toast.error('Please enter or select an email address');
      return;
    }
    
    // Warn if using suggestion without confirmation
    if (selectedEmailSource === 'suggestion' && !confirmSuggestion) {
      toast.error('Please confirm the suggested email is correct before proceeding');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/verification-email/preview`, {
        case_id: check.case_id,
        check_id: check.check_id || check.id,
        recipient_type: isEmployment ? 'hr' : isEducation ? 'university' : 'reference',
        recipient_email: email,
        recipient_name: getRecipientName(),
        cc_emails: ccEmails,
        include_documents: selectedDocuments,
        custom_remarks: customRemarks,
      });
      
      setPreviewData(response.data);
      setShowPreview(true);
      toast.success('Preview generated');
    } catch (error) {
      console.error('Failed to generate preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    const email = getRecipientEmail();
    if (!email) {
      toast.error('Please enter or select an email address');
      return;
    }

    setSending(true);
    try {
      // Send the verification email
      const response = await axios.post(`${API_URL}/api/initiate-email/send`, {
        case_id: check.case_id,
        check_id: check.check_id || check.id,
        check_type: check.check_type,
        recipient_email: email,
        recipient_name: getRecipientName(),
        email_source: selectedEmailSource,
        cc_emails: ccEmails,
        include_documents: selectedDocuments,
        custom_remarks: customRemarks,
        save_to_database: selectedEmailSource === 'manual' && saveToDatabase,
        send_acknowledgement: isReference && sendAcknowledgement,
        // Company/University info for database save
        company_name: check.subcheck_data?.company_name || check.subcheck_company,
        university_name: check.subcheck_data?.university_name || check.subcheck_institution,
      });
      
      toast.success(`Email sent successfully! Tracking ID: ${response.data.tracking_id}`);
      
      if (response.data.saved_to_database) {
        toast.info(`Email saved to ${isEmployment ? 'company' : 'university'} database`);
      }
      
      // Refresh email history after send
      await loadEmailHistory();
      
      // Close dialog on first send, keep open on resend for convenience
      if (!isResend) {
        onOpenChange(false);
      }
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const getCheckTypeLabel = () => {
    if (isEmployment) return 'Employment Verification';
    if (isEducation) return 'Education Verification';
    if (isReference) return 'Reference Check';
    return 'Verification';
  };

  const getRecipientTypeLabel = () => {
    if (isEmployment) return 'HR / Company';
    if (isEducation) return 'University / College';
    if (isReference) return 'Reference Contact';
    return 'Recipient';
  };

  const getEntityName = () => {
    if (isEmployment) return check?.subcheck_data?.company_name || check?.subcheck_company || 'Company';
    if (isEducation) return check?.subcheck_data?.university_name || check?.subcheck_institution || 'University';
    if (isReference) return check?.subcheck_data?.reference_name || 'Reference';
    return '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isResend ? (
              <><RefreshCw className="w-5 h-5 text-blue-600" />Resend {getCheckTypeLabel()}</>
            ) : (
              <><Send className="w-5 h-5 text-green-600" />Initiate {getCheckTypeLabel()}</>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && !emailOptions.length ? (
          <div className="py-8 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
            <p className="text-slate-600">Loading email options...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Check Info */}
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="py-3">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Candidate:</span>
                    <p className="font-medium">{check?.employee_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">{isEmployment ? 'Company' : isEducation ? 'University' : 'Contact'}:</span>
                    <p className="font-medium text-blue-600">{getEntityName()}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Case ID:</span>
                    <p className="font-mono text-xs">{check?.case_id}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email History (shown for resends) */}
            {emailHistory.length > 0 && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-800">Email History ({emailHistory.length} sent)</span>
                  </div>
                  <div className="space-y-1.5 max-h-28 overflow-y-auto">
                    {emailHistory.map((log, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1.5 border border-blue-100">
                        <div className="flex items-center gap-2">
                          <Mail className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-700">{log.recipient_email || log.to_email || '—'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400">
                            {log.sent_at ? new Date(log.sent_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            log.status === 'sent' || log.status === 'delivered' ? 'bg-green-100 text-green-700' :
                            log.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                          }`}>{log.status || 'sent'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Email Source Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Select {getRecipientTypeLabel()} Email
              </Label>
              
              <RadioGroup 
                value={selectedEmailSource} 
                onValueChange={handleEmailSourceChange}
                className="space-y-2"
              >
                {/* Database Option */}
                {emailOptions.filter(o => o.source === 'database').map((option, idx) => (
                  <div 
                    key={`db-${idx}`}
                    className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-colors ${
                      selectedEmailSource === 'database' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-green-300'
                    }`}
                  >
                    <RadioGroupItem value="database" id="database" />
                    <Label htmlFor="database" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Database className="w-4 h-4 text-green-600" />
                          <span className="font-medium">From {isEmployment ? 'Company' : 'University'} Database</span>
                          <Badge className="bg-green-100 text-green-700 text-xs">Recommended</Badge>
                        </div>
                        <span className="text-green-700 font-medium">{option.email}</span>
                      </div>
                      {option.name && (
                        <p className="text-xs text-slate-500 mt-1 ml-6">{option.name}</p>
                      )}
                    </Label>
                  </div>
                ))}

                {/* Candidate Provided Option */}
                {emailOptions.filter(o => o.source === 'candidate_provided').map((option, idx) => (
                  <div 
                    key={`cp-${idx}`}
                    className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-colors ${
                      selectedEmailSource === 'candidate_provided' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-blue-300'
                    }`}
                  >
                    <RadioGroupItem value="candidate_provided" id="candidate_provided" />
                    <Label htmlFor="candidate_provided" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-blue-600" />
                          <span className="font-medium">Candidate Provided</span>
                        </div>
                        <span className="text-blue-700 font-medium">{option.email}</span>
                      </div>
                      {option.name && (
                        <p className="text-xs text-slate-500 mt-1 ml-6">{option.name}</p>
                      )}
                    </Label>
                  </div>
                ))}

                {/* Manual Entry Option */}
                <div 
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    selectedEmailSource === 'manual' 
                      ? 'border-amber-500 bg-amber-50' 
                      : 'border-slate-200 hover:border-amber-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="manual" id="manual" />
                    <Label htmlFor="manual" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Edit className="w-4 h-4 text-amber-600" />
                        <span className="font-medium">Enter Email Manually</span>
                      </div>
                    </Label>
                  </div>
                  
                  {selectedEmailSource === 'manual' && (
                    <div className="mt-3 ml-6 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Email Address *</Label>
                          <Input
                            type="email"
                            placeholder={isEmployment ? "hr@company.com" : "registrar@university.edu"}
                            value={manualEmail}
                            onChange={(e) => setManualEmail(e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Recipient Name</Label>
                          <Input
                            placeholder={isEmployment ? "HR Department" : "Registrar Office"}
                            value={manualRecipientName}
                            onChange={(e) => setManualRecipientName(e.target.value)}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 bg-amber-100 p-2 rounded">
                        <Checkbox
                          id="saveToDb"
                          checked={saveToDatabase}
                          onCheckedChange={setSaveToDatabase}
                        />
                        <Label htmlFor="saveToDb" className="text-xs text-amber-800 cursor-pointer">
                          <Save className="w-3 h-3 inline mr-1" />
                          Save this email to {isEmployment ? 'Company' : 'University'} database for future use
                        </Label>
                      </div>
                    </div>
                  )}
                </div>
              </RadioGroup>
              
              {/* Email Suggestions (UNVERIFIED) */}
              {emailSuggestions.length > 0 && !isReference && (
                <div className="mt-4">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowSuggestions(!showSuggestions)}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {showSuggestions ? 'Hide' : 'Show'} Email Suggestions ({emailSuggestions.length})
                  </Button>
                  
                  {showSuggestions && (
                    <div className="mt-2 p-3 bg-red-50 border-2 border-red-200 rounded-lg">
                      <div className="flex items-start gap-2 mb-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-red-700">⚠️ UNVERIFIED EMAIL SUGGESTIONS</p>
                          <p className="text-xs text-red-600 mt-1">
                            These are auto-generated patterns based on {isEmployment ? 'company' : 'university'} name. 
                            <strong> DO NOT send sensitive data without verifying the email is correct.</strong>
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        {emailSuggestions.map((suggestion, idx) => (
                          <div 
                            key={idx}
                            className={`p-2 rounded border cursor-pointer transition-all ${
                              selectedSuggestion?.email === suggestion.email
                                ? 'border-red-500 bg-red-100'
                                : 'border-red-200 bg-white hover:border-red-400'
                            }`}
                            onClick={() => handleSelectSuggestion(suggestion)}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-mono text-red-700">{suggestion.email}</span>
                              <Badge className="bg-red-100 text-red-600 text-[10px]">UNVERIFIED</Badge>
                            </div>
                            <p className="text-[10px] text-red-500 mt-1">Pattern: {suggestion.pattern}@domain</p>
                          </div>
                        ))}
                      </div>
                      
                      {/* Confirmation for selected suggestion */}
                      {selectedSuggestion && selectedEmailSource === 'suggestion' && (
                        <div className="mt-3 p-3 bg-white rounded border border-red-300">
                          <p className="text-sm font-medium text-red-700 mb-2">
                            Selected: <span className="font-mono">{selectedSuggestion.email}</span>
                          </p>
                          
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id="confirmSuggestion"
                                checked={confirmSuggestion}
                                onCheckedChange={handleConfirmSuggestion}
                              />
                              <Label htmlFor="confirmSuggestion" className="text-xs text-red-800 cursor-pointer leading-tight">
                                I have <strong>independently verified</strong> that this email address is correct and belongs to the intended recipient. 
                                I understand that sending data to an incorrect email may result in data breach.
                              </Label>
                            </div>
                            
                            {confirmSuggestion && (
                              <div className="flex items-center space-x-2 bg-green-100 p-2 rounded">
                                <Checkbox
                                  id="saveToDbSuggestion"
                                  checked={saveToDatabase}
                                  onCheckedChange={setSaveToDatabase}
                                />
                                <Label htmlFor="saveToDbSuggestion" className="text-xs text-green-800 cursor-pointer">
                                  <Save className="w-3 h-3 inline mr-1" />
                                  Save this verified email to database for future use
                                </Label>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* CC Recipients */}
            <div className="space-y-2">
              <Label className="text-sm">CC Recipients (Optional)</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Add CC email..."
                  value={newCcEmail}
                  onChange={(e) => setNewCcEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCc()}
                  className="flex-1"
                />
                <Button type="button" onClick={handleAddCc} size="sm" variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {ccEmails.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {ccEmails.map((email, idx) => (
                    <Badge key={idx} variant="secondary" className="pl-2">
                      {email}
                      <button onClick={() => handleRemoveCc(email)} className="ml-1 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Reference Check - Acknowledgement Option */}
            {isReference && (
              <div className="flex items-center space-x-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <Checkbox
                  id="sendAck"
                  checked={sendAcknowledgement}
                  onCheckedChange={setSendAcknowledgement}
                />
                <Label htmlFor="sendAck" className="text-sm text-purple-800 cursor-pointer">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Send acknowledgement email to reference after verification
                </Label>
              </div>
            )}

            {/* Additional Remarks */}
            <div className="space-y-2">
              <Label className="text-sm">Additional Remarks (Optional)</Label>
              <Textarea
                placeholder="Add any additional notes or instructions..."
                value={customRemarks}
                onChange={(e) => setCustomRemarks(e.target.value)}
                rows={2}
              />
            </div>

            {/* Document Attachments */}
            {availableDocuments.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments ({selectedDocuments.length} selected)
                </Label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-slate-50 rounded-lg">
                  {availableDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors text-sm ${
                        selectedDocuments.includes(doc.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-white hover:border-blue-300'
                      }`}
                      onClick={() => handleToggleDocument(doc.id)}
                    >
                      <Checkbox
                        checked={selectedDocuments.includes(doc.id)}
                        onCheckedChange={() => handleToggleDocument(doc.id)}
                      />
                      <FileText className="w-3 h-3 text-slate-400" />
                      <span className="truncate flex-1">{doc.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Preview Section */}
            {showPreview && previewData && (
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader className="py-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Email Preview
                  </CardTitle>
                </CardHeader>
                <CardContent className="py-2 space-y-2 text-sm">
                  <div><strong>From:</strong> {previewData.sender_email}</div>
                  <div><strong>To:</strong> {previewData.recipient_email}</div>
                  {previewData.cc_emails?.length > 0 && (
                    <div><strong>CC:</strong> {previewData.cc_emails.join(', ')}</div>
                  )}
                  <div><strong>Subject:</strong> {previewData.subject}</div>
                  <div><strong>Tracking ID:</strong> <Badge variant="outline">{previewData.tracking_id}</Badge></div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            variant="outline" 
            onClick={handlePreview} 
            disabled={loading || !canSendEmail()}
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
            Preview
          </Button>
          <Button 
            onClick={handleSendEmail}
            disabled={sending || !canSendEmail()}
            className={isResend ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"}
          >
            {sending ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : isResend ? (
              <RefreshCw className="w-4 h-4 mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {isResend ? 'Resend Email' : 'Send Email'}
          </Button>
        </DialogFooter>
        
        {/* Security Warning */}
        {selectedEmailSource === 'suggestion' && !confirmSuggestion && (
          <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-center">
            <p className="text-xs text-red-700">
              <AlertCircle className="w-3 h-3 inline mr-1" />
              Please confirm the suggested email is verified before sending
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default InitiateVerificationEmailDialog;
